import React, { useEffect, useRef, useState } from 'react'
import html2canvas from 'html2canvas'
import { BOOKMAKER_LOGOS } from '../config/bookmakers'
import { MARKET_TYPE_MAPPING } from '../config/markets'
import { SPORT_DISPLAY_NAMES } from '../config/sports'
import { settlementFields } from '../TRACKER_PAGE/settlement'
import { subjectFor } from '../TRACKER_PAGE/LiveBetMeter'
import { SPORT_GROUP, SPORT_GLYPHS, PERSON_GLYPH } from '../components/SubjectImage'

const LOGO_URL = 'https://nfoqikkzbosxfylfstbz.supabase.co/storage/v1/object/public/images/BC_LOGO_200x200.png'

// Pull an over/under direction + line out of a selection like "Over 210.5" so we can
// colour-code the side. Team/H2H selections (e.g. "Carlton") return dir=null and render plain.
const parseDirection = (sel) => {
  const m = sel?.match(/\b(over|under)\b\s*(-?\d+(?:\.\d+)?)?/i)
  if (!m) return { dir: null, line: null }
  return { dir: m[1].toLowerCase(), line: m[2] || null }
}

// Subject = the team name, which is always whatever comes BEFORE "over/under". Everything
// from "over/under" onward is the direction + line + repeated market wording (e.g. "Under
// 3.5 Total Runs"), all of which belongs in the badges, not the heading. So:
//   "Baltimore Orioles Under 3.5 Total Runs" → "Baltimore Orioles"
//   "Over 10.5"                              → ""  (non-team total: heading omitted)
//   "Toronto Tempo" (H2H, no over/under)     → "Toronto Tempo"
const subjectOf = (sel) => {
  const s = sel || ''
  const idx = s.search(/\b(over|under)\b/i)
  return (idx === -1 ? s : s.slice(0, idx)).replace(/\s+/g, ' ').trim()
}

// What a multi leg's line is measured in, so the bet reads as "Over 1.5 Points"
// rather than a bare "Over 1.5". Prefers the wording the selection already
// carries after its line ("... Over 25.5 Disposals" → "Disposals"); failing
// that, the market's display name with the "Player" prefix and "Milestones"
// suffix dropped, since the player's name already sits above it.
const unitOf = (sel, marketType) => {
  const tail = (sel || '').match(/\b(?:over|under)\b\s*-?\d+(?:\.\d+)?\s+(.+)$/i)
  if (tail) return tail[1].trim()
  const base = (marketType || '').replace('_milestones', '')
  const label = MARKET_TYPE_MAPPING[base] || MARKET_TYPE_MAPPING[marketType] || ''
  return label.replace(/^Player\s+/i, '').replace(/\s+Milestones?$/i, '').trim()
}

// The second line under a multi leg's subject. O/U legs get side + line + unit;
// anything else (h2h, handicap) gets the market name, so a bare team name still
// says what was backed.
const legSideLine = (sel, marketType) => {
  const { dir, line } = parseDirection(sel)
  if (!dir) return MARKET_TYPE_MAPPING[marketType] || ''
  const side = dir === 'over' ? 'Over' : 'Under'
  return [side, line, unitOf(sel, marketType)].filter(Boolean).join(' ')
}

/**
 * One image inside a ShareSubject box.
 *
 * `cover` (headshots) must fill the box in BOTH axes, and which axis does the
 * filling depends on the photo: width:100% fills a PORTRAIT source but leaves a
 * gap beneath a landscape or square one — that gap under the player. min-width /
 * min-height cannot fix it either, since mins only push a box UP and a source
 * larger than the frame ignores them. So the aspect is measured on load and the
 * fill axis chosen from it. That is object-fit: cover, which html2canvas does
 * not implement, done by hand.
 *
 * Crests instead fit INSIDE the box, because a cropped mark is a broken logo.
 */
function CoverImg({ src, cover, half, first, onFail }) {
  const [landscape, setLandscape] = useState(null)

  // A pair OVERLAPS rather than sitting side by side, the way the app draws it.
  // Percentages resolve against the flex container's content box, so 62% each
  // with the second pulled back 24% lands the two of them exactly across the
  // full width (62 + 62 - 24 = 100). Done with a negative margin rather than
  // absolute positioning because html2canvas walks normal flow reliably and the
  // second image, being later in the DOM, paints over the first for free.
  const style = !cover
    ? half
      ? { maxWidth: '62%', maxHeight: '100%', display: 'block', marginLeft: first ? 0 : '-24%' }
      : { maxWidth: '100%', maxHeight: '100%', display: 'block' }
    : landscape
      ? { height: '100%', width: 'auto', display: 'block' }   // wider than tall: fill height, crop the sides
      : { width: '100%', height: 'auto', display: 'block' }   // taller than wide: fill width, crop the bottom

  return (
    <img
      src={src}
      alt=""
      crossOrigin="anonymous"
      onLoad={e => { if (cover) setLandscape(e.target.naturalWidth > e.target.naturalHeight) }}
      onError={onFail}
      style={style}
    />
  )
}

/**
 * Subject image for the share card. Deliberately NOT the SubjectImage used by the
 * table and the modals, even though it asks subjectFor() the same question.
 *
 * html2canvas 1.4.1 does not implement `object-fit` (long-standing open issue),
 * and every SubjectImage image relies on it — a crest would be stretched to a
 * square and a headshot squashed. Aspect is preserved here the only way the
 * capture understands: a real layout box with max-width/max-height, so the
 * browser sizes the <img> itself and html2canvas just draws that box.
 *
 * Two other capture-specific differences:
 *   - crossOrigin is required, or the canvas is tainted and toDataURL throws
 *   - the frame is a rounded SQUARE, not the app's circle. A circle only works
 *     with cover-cropping, which is exactly what is unavailable here, so a
 *     portrait headshot would letterbox inside it.
 */
function ShareSubject({ opp, size = 66 }) {
  const sport = opp?.sport
  const subject = subjectFor({ ...settlementFields(opp), sport })
  const [failed, setFailed] = useState(0)
  const urls = subject?.urls ?? []
  const dead = urls.length === 0 || failed >= urls.length

  // A headshot must FILL the box; a crest must fit inside it. object-fit would
  // say that in one word, but html2canvas ignores it, so the two cases are laid
  // out differently instead:
  //   player -> width 100%, top aligned, box clips the overflow. That is
  //             object-fit: cover by hand, and it is what keeps the head large.
  //             Fitting by height (max-height) is what made them look tiny: a
  //             portrait photo letterboxes, so the face ends up a fraction of
  //             the square with padding all round it.
  //   crest  -> max-width/max-height with padding, i.e. contain, because a mark
  //             cropped at the edges is a broken logo.
  const isPlayer = subject?.kind === 'player'
  const pad = Math.round(size * 0.12)
  const box = {
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: `${Math.round(size * 0.22)}px`,
    background: '#101a2e',
    border: '1px solid #22304c',
    display: 'flex',
    alignItems: isPlayer ? 'flex-start' : 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
    padding: isPlayer ? 0 : `${pad}px`,
    boxSizing: 'border-box'
  }

  if (dead) {
    const group = SPORT_GROUP[sport]
    const glyph = subject?.kind === 'player' ? PERSON_GLYPH : (group ? SPORT_GLYPHS[group] : null)
    if (!glyph) return <div style={box} />
    return (
      <div style={{ ...box, color: '#46566f', padding: `${pad}px`, alignItems: 'center' }}>
        <svg viewBox="0 0 24 24" width={Math.round(size * 0.62)} height={Math.round(size * 0.62)} aria-hidden="true">
          {glyph}
        </svg>
      </div>
    )
  }

  return (
    <div style={box}>
      {urls.map((u, i) => (
        <CoverImg
          key={i}
          src={`${u}?cors=1`}
          cover={isPlayer}
          half={urls.length > 1}
          first={i === 0}
          onFail={() => setFailed(n => n + 1)}
        />
      ))}
    </div>
  )
}

// `showMarket` off drops "The rest of the market" strip. Prop EV turns it off:
// the card is otherwise shared so both pages' share images stay identical.
export default function ShareModal({ bet, multi, onClose, showMarket = true }) {
  const cardRef = useRef(null)
  const [imageUrl, setImageUrl] = useState(null)
  const [capturing, setCapturing] = useState(true)
  const [copied, setCopied] = useState(false)

  const isMulti = !!multi
  const selection = bet?.selection || ''
  const { dir, line } = parseDirection(selection)
  const marketLabel = MARKET_TYPE_MAPPING[bet?.market_type] || bet?.market_type || ''
  const sportLabel = SPORT_DISPLAY_NAMES[bet?.sport] || bet?.sport || ''
  // bookmakers is an array of name strings on desktop EV but { name } objects on
  // mobile EV — tolerate both so one modal serves both pages.
  const firstBook = bet?.bookmakers?.[0]
  const bestBook = (typeof firstBook === 'string' ? firstBook : firstBook?.name) || bet?.bookmaker || ''
  const oddsValue = bet?.original_odds ? Number(bet.original_odds).toFixed(2) : '-'
  const evValue = bet?.ev_percentage != null ? `+${bet.ev_percentage.toFixed(2)}%` : ''

  // Comparison books — the other books from the fair-odds calc (the picked book is the hero
  // in the odds row above, so exclude it here). Selection-side price: under_odds for an under
  // pick (when present), else odds.
  const COMPARISON_CAP = 8
  const comparisonBooks = (() => {
    const entries = Object.entries(bet?.all_bookmaker_odds || {})
      .filter(([book]) => book !== bestBook)
      .map(([book, d]) => {
        const price = dir === 'under' && d?.under_odds != null ? d.under_odds : d?.odds
        return { book, price }
      })
      .filter(b => typeof b.price === 'number')
      .sort((a, b) => b.price - a.price)
    return { shown: entries.slice(0, COMPARISON_CAP), extra: Math.max(0, entries.length - COMPARISON_CAP) }
  })()

  // Multi values
  const legs = multi?.legs || []
  const multiOdds = multi?.finalOdds != null ? Number(multi.finalOdds).toFixed(2) : '-'
  const multiEv = multi?.combinedEV != null ? `+${multi.combinedEV.toFixed(2)}%` : ''

  // Group legs by match for the multi share card
  const legGroups = (() => {
    const map = new Map()
    legs.forEach(leg => {
      const key = `${leg.commence_time}|${leg.match}`
      if (!map.has(key)) map.set(key, { match: leg.match, legs: [] })
      map.get(key).legs.push(leg)
    })
    return [...map.values()]
  })()

  // Pick the most common bookmaker across legs (for the combined odds row)
  const dominantBook = (() => {
    if (!legs.length) return ''
    const counts = {}
    legs.forEach(leg => { if (leg.bookmaker) counts[leg.bookmaker] = (counts[leg.bookmaker] || 0) + 1 })
    return Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || ''
  })()

  useEffect(() => {
    if (!bet && !multi) return
    // Wait one tick so the off-screen card mounts and images start loading
    const timer = setTimeout(async () => {
      try {
        const canvas = await html2canvas(cardRef.current, {
          backgroundColor: null,
          scale: 2,
          useCORS: true,
          allowTaint: false,
          logging: false
        })
        setImageUrl(canvas.toDataURL('image/png'))
      } catch (err) {
        console.error('Share capture failed:', err)
      } finally {
        setCapturing(false)
      }
    }, 900)
    return () => clearTimeout(timer)
  }, [bet, multi])

  const buildFilename = () => {
    const slug = isMulti
      ? `multi-${legs.length}leg`
      : (selection.replace(/\s+/g, '-').toLowerCase() || 'ev-bet')
    return `bookiecharity-${slug}.png`
  }

  const handleDownload = () => {
    if (!imageUrl) return
    const a = document.createElement('a')
    a.href = imageUrl
    a.download = buildFilename()
    a.click()
  }

  const handleNativeShare = async () => {
    if (!imageUrl) return
    try {
      const blob = await (await fetch(imageUrl)).blob()
      const file = new File([blob], buildFilename(), { type: 'image/png' })
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: 'Check out this +EV bet' })
        return
      }
      // Fallback to download if share unsupported / blocked
      handleDownload()
    } catch (err) {
      // User cancelled the share sheet (AbortError) — silently ignore
      if (err?.name === 'AbortError') return
      console.error('Native share failed:', err)
      handleDownload()
    }
  }

  const handleCopy = async () => {
    if (!imageUrl) return
    try {
      const blob = await (await fetch(imageUrl)).blob()
      await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })])
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Copy failed:', err)
      // Fallback: download
      handleDownload()
    }
  }

  if (!bet && !multi) return null

  return (
    <>
      {/* Off-screen render target. html2canvas captures this. */}
      <div style={{
        position: 'fixed',
        left: '-9999px',
        top: 0,
        pointerEvents: 'none',
        opacity: 1
      }}>
        <div
          ref={cardRef}
          style={{
            width: '600px',
            padding: '0 0 26px',
            /* One deep base with a single soft light source, instead of a 3-stop
               gradient plus a cyan blob. A share card is screenshotted onto other
               people's feeds, so it has to read as a product, not as a wallpaper. */
            background: '#0b1220',
            backgroundImage: 'radial-gradient(700px 320px at 12% -8%, rgba(37, 99, 235, 0.16), rgba(37, 99, 235, 0) 62%)',
            border: '1px solid #1e2b45',
            borderRadius: '20px',
            position: 'relative',
            overflow: 'hidden',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            color: '#f1f5f9'
          }}
        >
          {/* Top hairline in the accent — the one flourish, and it doubles as the
              brand mark running the full width. */}
          <div style={{
            height: '3px',
            background: 'linear-gradient(90deg, #22d3ee 0%, #3b82f6 45%, rgba(59, 130, 246, 0) 100%)'
          }} />

          {/* ── Header ─────────────────────────────────────────────────────── */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '18px 30px',
            borderBottom: '1px solid #16223a'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
              <img
                src={LOGO_URL}
                alt="Bookie Charity"
                crossOrigin="anonymous"
                style={{ width: '32px', height: '32px', borderRadius: '8px', display: 'block' }}
              />
              <div style={{
                fontSize: '13px',
                fontWeight: '700',
                color: '#e2e8f0',
                letterSpacing: '0.14em',
                textTransform: 'uppercase'
              }}>
                Bookie Charity
              </div>
            </div>
            <div style={{
              fontSize: '10.5px',
              fontWeight: '700',
              color: '#5b6d88',
              letterSpacing: '0.16em',
              textTransform: 'uppercase'
            }}>
              {isMulti ? `${legs.length}-Leg Multi` : (sportLabel || 'EV Bet')}
            </div>
          </div>

          {/* ── Single-bet body ────────────────────────────────────────────── */}
          {!isMulti && bet && (<>
          <div style={{ padding: '26px 30px 0', display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
            {marketLabel && (
              <div style={{
                fontSize: '11px',
                fontWeight: '700',
                color: '#60a5fa',
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                marginBottom: '10px'
              }}>
                {marketLabel}
              </div>
            )}

            {/* The bet as one statement, not a heading plus a chip: the subject on
                top, the side and line beneath it in the accent. A chip made the
                line look like metadata when it IS the bet. */}
            {(() => {
              const subject = dir ? subjectOf(selection) : selection
              return (
                <>
                  {subject && (
                    <div style={{
                      fontSize: '34px',
                      fontWeight: '800',
                      color: '#ffffff',
                      letterSpacing: '-0.03em',
                      lineHeight: 1.08
                    }}>
                      {subject}
                    </div>
                  )}
                  {dir && (
                    <div style={{
                      fontSize: '21px',
                      fontWeight: '700',
                      color: '#93c5fd',
                      letterSpacing: '-0.015em',
                      marginTop: subject ? '5px' : 0,
                      textTransform: 'capitalize'
                    }}>
                      {dir}{line ? ` ${line}` : ''}
                    </div>
                  )}
                </>
              )
            })()}

            <div style={{ fontSize: '13px', color: '#5b6d88', fontWeight: '500', marginTop: '10px' }}>
              {bet.match}
            </div>
            </div>
            {/* Right, not left: the headline is the bet, so it should own the
                left edge the market label and fixture already sit on. Centred,
                so a two-line and a three-line headline both look deliberate. */}
            <ShareSubject opp={bet} size={86} />
          </div>

          {/* ── Proof panel ────────────────────────────────────────────────────
              The centrepiece, and the thing the old card never showed. "+4.52%"
              on its own is a claim; the price you get NEXT TO the fair price is
              the evidence for it, and that is the whole product in one line.
              Falls back to two columns when the engine sent no fair_odds. */}
          {(() => {
            const fair = typeof bet.fair_odds === 'number' ? bet.fair_odds.toFixed(2) : null
            // An UNLABELLED cell drops the label line entirely and centres itself
            // VERTICALLY in the cell, rather than reserving an empty line and
            // sitting low under it. It still starts on the same left edge as the
            // labelled cells, so the panel keeps one column of content.
            const cell = (label, value, color, extra) => (
              <div style={{
                flex: 1,
                padding: '16px 18px',
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>
                {label && (
                  <div style={{
                    fontSize: '10px',
                    fontWeight: '700',
                    color: '#5b6d88',
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    lineHeight: '12px',
                    height: '12px',
                    marginBottom: '9px',
                    whiteSpace: 'nowrap'
                  }}>
                    {label}
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  {extra}
                  <span style={{
                    fontSize: '30px',
                    fontWeight: '800',
                    color,
                    letterSpacing: '-0.03em',
                    lineHeight: 1,
                    fontVariantNumeric: 'tabular-nums'
                  }}>
                    {value}
                  </span>
                </div>
              </div>
            )
            return (
              <div style={{
                display: 'flex',
                alignItems: 'stretch',
                margin: '22px 30px 0',
                background: 'rgba(15, 23, 42, 0.72)',
                border: '1px solid #22304c',
                borderRadius: '14px',
                overflow: 'hidden'
              }}>
                {cell(
                  null,
                  oddsValue,
                  '#ffffff',
                  BOOKMAKER_LOGOS[bestBook] ? (
                    <img
                      src={BOOKMAKER_LOGOS[bestBook]}
                      alt={bestBook}
                      crossOrigin="anonymous"
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '10px',
                        display: 'block',
                        flexShrink: 0,
                        border: '1px solid rgba(148, 163, 184, 0.28)',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.5)'
                      }}
                    />
                  ) : null
                )}
                {fair && (
                  <>
                    <div style={{ width: '1px', background: '#22304c' }} />
                    {cell('Fair price', fair, '#8b9cb8')}
                  </>
                )}
                <div style={{ width: '1px', background: '#22304c' }} />
                <div style={{ flex: 1, padding: '16px 18px', background: 'rgba(16, 185, 129, 0.07)', minWidth: 0 }}>
                  <div style={{
                    fontSize: '10px',
                    fontWeight: '700',
                    color: '#34d399',
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    lineHeight: '12px',
                    height: '12px',
                    marginBottom: '9px',
                    whiteSpace: 'nowrap'
                  }}>
                    EV
                  </div>
                  <span style={{
                    fontSize: '30px',
                    fontWeight: '800',
                    color: '#34d399',
                    letterSpacing: '-0.03em',
                    lineHeight: 1,
                    fontVariantNumeric: 'tabular-nums'
                  }}>
                    {evValue}
                  </span>
                </div>
              </div>
            )
          })()}

          {/* ── The field ──────────────────────────────────────────────────────
              Was white circles with blue glow rings and floating shadowed pills —
              three treatments fighting on one strip. It is EVIDENCE, so it should
              be quiet and let the reader compare numbers: same tile as everything
              else, price directly beneath, nothing glowing. */}
          {showMarket && comparisonBooks.shown.length > 0 && (
            <div style={{ padding: '24px 30px 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
                <span style={{
                  fontSize: '10px',
                  fontWeight: '700',
                  color: '#5b6d88',
                  letterSpacing: '0.16em',
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap'
                }}>
                  The rest of the market
                </span>
                <div style={{ flex: 1, height: '1px', background: '#16223a' }} />
              </div>

              <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                {comparisonBooks.shown.map(({ book, price }) => (
                  <div key={book} style={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
                    <div style={{
                      width: '38px',
                      height: '38px',
                      margin: '0 auto 7px',
                      borderRadius: '10px',
                      background: '#101a2e',
                      border: '1px solid #22304c',
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {BOOKMAKER_LOGOS[book] ? (
                        <img
                          src={BOOKMAKER_LOGOS[book]}
                          alt={book}
                          crossOrigin="anonymous"
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                      ) : (
                        <span style={{ fontSize: '12px', fontWeight: '800', color: '#8b9cb8' }}>
                          {book.slice(0, 2).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div style={{
                      fontSize: '12.5px',
                      fontWeight: '700',
                      color: '#8b9cb8',
                      fontVariantNumeric: 'tabular-nums',
                      letterSpacing: '-0.01em'
                    }}>
                      {Number(price).toFixed(2)}
                    </div>
                  </div>
                ))}
                {comparisonBooks.extra > 0 && (
                  <div style={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
                    <div style={{
                      width: '38px',
                      height: '38px',
                      margin: '0 auto 7px',
                      borderRadius: '10px',
                      border: '1px dashed #22304c',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: '800',
                      color: '#5b6d88'
                    }}>
                      +{comparisonBooks.extra}
                    </div>
                    <div style={{ fontSize: '12.5px', fontWeight: '700', color: '#3f4f66' }}>more</div>
                  </div>
                )}
              </div>
            </div>
          )}
          </>)}

          {/* ── Multi body ─────────────────────────────────────────────────── */}
          {isMulti && (
            <>
              <div style={{ padding: '26px 30px 0' }}>
                <div style={{
                  fontSize: '11px',
                  fontWeight: '700',
                  color: '#60a5fa',
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  marginBottom: '10px'
                }}>
                  {legs.length} legs · {legGroups.length} {legGroups.length === 1 ? 'game' : 'games'}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {legGroups.map((group, gi) => (
                    <div key={gi} style={{
                      background: 'rgba(15, 23, 42, 0.6)',
                      border: '1px solid #22304c',
                      borderRadius: '12px',
                      padding: '13px 15px'
                    }}>
                      <div style={{
                        fontSize: '10.5px',
                        fontWeight: '700',
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        color: '#5b6d88',
                        marginBottom: '11px'
                      }}>
                        {group.match}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
                        {group.legs.map((leg, li) => {
                          const { dir: legDir } = parseDirection(leg.selection)
                          const legMarketLabel = MARKET_TYPE_MAPPING[leg.market_type] || leg.market_type || ''
                          // A game total ("Over 150.5") has no subject of its own, so the
                          // side line becomes the headline instead of repeating the market.
                          const legSubject = legDir ? subjectOf(leg.selection) : leg.selection
                          const fullSide = legSideLine(leg.selection, leg.market_type)
                          const subject = legSubject || fullSide || legMarketLabel
                          const sideLine = legSubject ? fullSide : ''
                          return (
                            <div key={li} style={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
                              <ShareSubject opp={leg} size={42} />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: '15px', fontWeight: '700', color: '#f1f5f9', letterSpacing: '-0.015em', lineHeight: 1.2 }}>
                                  {subject}
                                </div>
                                {sideLine && (
                                  <div style={{ fontSize: '13px', fontWeight: '600', color: legDir ? '#93c5fd' : '#8b9cb8', marginTop: '2px' }}>
                                    {sideLine}
                                  </div>
                                )}
                              </div>
                              <div style={{
                                fontSize: '16px',
                                fontWeight: '800',
                                color: '#e2e8f0',
                                letterSpacing: '-0.02em',
                                fontVariantNumeric: 'tabular-nums',
                                flexShrink: 0
                              }}>
                                {Number(leg.odds).toFixed(2)}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Same proof panel shape as a single, so both cards read alike. */}
              <div style={{
                display: 'flex',
                alignItems: 'stretch',
                margin: '20px 30px 0',
                background: 'rgba(15, 23, 42, 0.72)',
                border: '1px solid #22304c',
                borderRadius: '14px',
                overflow: 'hidden'
              }}>
                <div style={{ flex: 1, padding: '16px 18px', minWidth: 0 }}>
                  <div style={{ fontSize: '10px', fontWeight: '700', color: '#5b6d88', letterSpacing: '0.14em', textTransform: 'uppercase', lineHeight: '12px', height: '12px', marginBottom: '9px', whiteSpace: 'nowrap' }}>
                    Combined odds
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
                    {dominantBook && BOOKMAKER_LOGOS[dominantBook] && (
                      <img
                        src={BOOKMAKER_LOGOS[dominantBook]}
                        alt={dominantBook}
                        crossOrigin="anonymous"
                        style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '10px',
                        display: 'block',
                        flexShrink: 0,
                        border: '1px solid rgba(148, 163, 184, 0.28)',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.5)'
                      }}
                      />
                    )}
                    <span style={{ fontSize: '30px', fontWeight: '800', color: '#ffffff', letterSpacing: '-0.03em', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                      {multiOdds}
                    </span>
                  </div>
                </div>
                <div style={{ width: '1px', background: '#22304c' }} />
                <div style={{ flex: 1, padding: '16px 18px', background: 'rgba(16, 185, 129, 0.07)', minWidth: 0 }}>
                  <div style={{ fontSize: '10px', fontWeight: '700', color: '#34d399', letterSpacing: '0.14em', textTransform: 'uppercase', lineHeight: '12px', height: '12px', marginBottom: '9px', whiteSpace: 'nowrap' }}>
                    EV
                  </div>
                  <span style={{ fontSize: '30px', fontWeight: '800', color: '#34d399', letterSpacing: '-0.03em', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                    {multiEv}
                  </span>
                </div>
              </div>
            </>
          )}

          {/* ── Footer ─────────────────────────────────────────────────────── */}
          <div style={{
            margin: '26px 30px 0',
            paddingTop: '16px',
            borderTop: '1px solid #16223a',
            fontSize: '13px',
            fontWeight: '600',
            letterSpacing: '0.005em',
            lineHeight: 1.2
          }}>
            <span style={{ color: '#94a3b8' }}>+EV bet found on </span>
            <span style={{ color: '#60a5fa', fontWeight: '800' }}>bookiecharity.com</span>
          </div>
        </div>
      </div>

      {/* Modal overlay */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(1, 4, 12, 0.72)',
          backdropFilter: 'blur(4px)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            // Same surfaces as the refined track / filter modals: #080e1b
            // shell, #0d1628 one step up, #1b2438 hairlines, Geist.
            background: '#080e1b',
            border: '1px solid #1b2438',
            borderRadius: '14px',
            padding: '24px',
            maxWidth: '520px',
            width: '100%',
            position: 'relative',
            boxShadow: '0 24px 72px rgba(0, 0, 0, 0.65)',
            fontFamily: "'Geist', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
            WebkitFontSmoothing: 'antialiased'
          }}
        >
          {/* Close */}
          <button
            onClick={onClose}
            aria-label="Close"
            onMouseEnter={(e) => { e.currentTarget.style.color = '#e2e8f0'; e.currentTarget.style.background = 'rgba(148, 163, 184, 0.08)' }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#5f6d82'; e.currentTarget.style.background = 'transparent' }}
            style={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              background: 'transparent',
              border: '1px solid transparent',
              color: '#5f6d82',
              cursor: 'pointer',
              transition: 'color 0.15s ease, background 0.15s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>

          <h3 style={{ fontSize: '17px', fontWeight: '600', color: '#f1f5f9', margin: '0 0 4px 0', letterSpacing: '-0.01em' }}>
            Share this bet
          </h3>
          <p style={{ fontSize: '13px', color: '#8b98ab', margin: '0 0 20px 0' }}>
            Download the image or share it anywhere.
          </p>

          {/* Preview */}
          <div style={{
            background: '#0d1628',
            border: '1px solid #1b2438',
            borderRadius: '12px',
            padding: '12px',
            marginBottom: '16px',
            minHeight: '200px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {capturing ? (
              <div style={{
                width: '100%',
                aspectRatio: '600 / 400',
                borderRadius: '8px',
                background: '#0d1628',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  border: '2.5px solid #1b2438',
                  borderTopColor: '#60a5fa',
                  borderRadius: '50%',
                  animation: 'shareSpin 0.8s linear infinite'
                }} />
                <div style={{ fontSize: '13px', color: '#5f6d82', fontWeight: '500' }}>Generating image...</div>
                <style>{`@keyframes shareSpin { to { transform: rotate(360deg); } }`}</style>
              </div>
            ) : imageUrl ? (
              <img src={imageUrl} alt="Bet share card" style={{ width: '100%', borderRadius: '8px', display: 'block' }} />
            ) : (
              <div style={{ color: '#ef4444', fontSize: '13px' }}>Failed to generate image</div>
            )}
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={handleCopy}
              disabled={!imageUrl}
              style={{
                flex: 1,
                padding: '11px 16px',
                background: '#0d1628',
                color: imageUrl ? '#cbd5e1' : '#3e4c63',
                border: '1px solid #1f2b42',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '500',
                cursor: imageUrl ? 'pointer' : 'not-allowed',
                fontFamily: 'inherit',
                letterSpacing: '0.01em',
                transition: 'all 0.15s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px'
              }}
              onMouseEnter={(e) => imageUrl && (e.currentTarget.style.background = '#121d33', e.currentTarget.style.borderColor = '#2b3a57', e.currentTarget.style.color = '#f1f5f9')}
              onMouseLeave={(e) => imageUrl && (e.currentTarget.style.background = '#0d1628', e.currentTarget.style.borderColor = '#1f2b42', e.currentTarget.style.color = '#cbd5e1')}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
              </svg>
              {copied ? 'Copied!' : 'Copy'}
            </button>
            <button
              onClick={handleDownload}
              disabled={!imageUrl}
              style={{
                flex: 1,
                padding: '11px 16px',
                background: '#0d1628',
                color: imageUrl ? '#cbd5e1' : '#3e4c63',
                border: '1px solid #1f2b42',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '500',
                cursor: imageUrl ? 'pointer' : 'not-allowed',
                fontFamily: 'inherit',
                letterSpacing: '0.01em',
                transition: 'all 0.15s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px'
              }}
              onMouseEnter={(e) => imageUrl && (e.currentTarget.style.background = '#121d33', e.currentTarget.style.borderColor = '#2b3a57', e.currentTarget.style.color = '#f1f5f9')}
              onMouseLeave={(e) => imageUrl && (e.currentTarget.style.background = '#0d1628', e.currentTarget.style.borderColor = '#1f2b42', e.currentTarget.style.color = '#cbd5e1')}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Download
            </button>
            <button
              onClick={handleNativeShare}
              disabled={!imageUrl}
              style={{
                flex: 1,
                padding: '11px 16px',
                background: imageUrl ? '#2563eb' : '#1b2438',
                color: imageUrl ? '#ffffff' : '#5f6d82',
                border: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: imageUrl ? 'pointer' : 'not-allowed',
                fontFamily: 'inherit',
                letterSpacing: '0.01em',
                boxShadow: 'none',
                transition: 'all 0.15s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px'
              }}
              onMouseEnter={(e) => imageUrl && (e.currentTarget.style.background = '#3b82f6')}
              onMouseLeave={(e) => imageUrl && (e.currentTarget.style.background = '#2563eb')}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
                <polyline points="16 6 12 2 8 6"/>
                <line x1="12" y1="2" x2="12" y2="15"/>
              </svg>
              Share
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
